/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';
import {
  ArrowCircleRightIcon,
  DocumentDuplicateIcon,
} from '@heroicons/react/outline';

import { withNotification } from 'components/Notification';
import { CustomDomainModal } from 'components/CustomDomainModal';
import { NotificationTypes, apiErrorHandler } from 'utils';
import { GET_STORE_TRACKING_PAGE_SETTINGS } from 'gql/queries';
import { UPDATE_STORE_TRACKING_PAGE_SETTINGS } from 'gql/mutations';

import { useStore } from 'context/store-context';

export const CustomSubDomain = withNotification(({ showNotification }: any) => {
  const { storeId } = useStore();

  const [isCopied, setIsCopied] = useState(false);
  const [showCustomDomainModal, setShowCustomDomainModal] =
    useState<boolean>(false);
  const [customDomain, setCustomDomain] = useState<string | null>(null);

  const [getCustomerTrackingPageSettings] = useLazyQuery(
    GET_STORE_TRACKING_PAGE_SETTINGS,
    {
      onError(error) {
        const newError = apiErrorHandler(error);
        showNotification(NotificationTypes.error, newError?.message);
      },
      onCompleted(data) {
        if (data.storesSettings.length) {
          setCustomDomain(data.storesSettings[0].customDomain);
        }
      },
    },
  );

  const [saveCustomDomain] = useMutation(UPDATE_STORE_TRACKING_PAGE_SETTINGS, {
    onError(error) {
      const newError = apiErrorHandler(error);
      showNotification(NotificationTypes.error, newError?.message);
    },
    onCompleted(data) {
      showNotification(
        NotificationTypes.success,
        'Your custom domain is updated successfully!',
      );
      setCustomDomain(data?.saveStoresSettings?.customDomain ?? '');
      setShowCustomDomainModal(false);
    },
  });

  useEffect(() => {
    const getTrackingPageSettings = async () => {
      try {
        if (storeId) {
          getCustomerTrackingPageSettings({
            fetchPolicy: 'cache-and-network',
            variables: {
              storeId,
            },
          });
        }
      } catch (e) {
        console.log('error', e);
      }
    };
    getTrackingPageSettings();
  }, []);

  const handleSaveCustomDomain = () => {
    saveCustomDomain({
      variables: {
        input: {
          storeId,
          customDomain,
        },
      },
    });
  };

  const handleDeleteCustomDomain = () => {
    saveCustomDomain({
      variables: {
        input: {
          storeId,
          customDomain: null,
        },
      },
    });
  };

  const handleCopy = () => {
    if (!navigator?.clipboard) return;

    navigator.clipboard.writeText(`https://${customDomain}.shipaid.com`);

    setIsCopied(true);

    setTimeout(() => {
      setIsCopied(false);
    }, 5000);
  };

  return (
    <>
      <section className="bg-white rounded-lg border border-[#e1e3e5] p-6">
        <div className="flex flex-col gap-5 md:flex-row md:items-start md:justify-between">
          <div className="max-w-2xl">
            <h2 className="text-base font-semibold text-[#202223] mb-1">
              Custom subdomain
            </h2>
            <p className="text-[#6d7175] text-sm">
              The branded address your customers use to track their orders.
            </p>

            {customDomain ? (
              <div className="mt-4 flex items-center gap-2 flex-wrap">
                <code className="text-sm font-medium text-[#202223] bg-[#f6f6f7] border border-[#e1e3e5] rounded-md px-3 py-1.5">
                  {`https://${customDomain}.shipaid.com`}
                </code>
                <button
                  type="button"
                  onClick={handleCopy}
                  aria-label="Copy tracking page link"
                  className="p-1.5 rounded-md border border-[#e1e3e5] hover:bg-[#f6f6f7]"
                >
                  <DocumentDuplicateIcon
                    className={isCopied ? 'text-blue-600' : 'text-[#6d7175]'}
                    width={18}
                    height={18}
                  />
                </button>
                <a
                  href={`https://${customDomain}.shipaid.com`}
                  target="_blank"
                  rel="noreferrer"
                  aria-label="Open tracking page"
                  className="p-1.5 rounded-md border border-[#e1e3e5] hover:bg-[#f6f6f7] inline-flex"
                >
                  <ArrowCircleRightIcon
                    className="text-[#6d7175]"
                    width={18}
                    height={18}
                  />
                </a>
                {isCopied ? (
                  <span className="text-xs text-[#6d7175]" role="status">
                    Copied
                  </span>
                ) : null}
              </div>
            ) : (
              <p className="mt-4 text-sm text-[#6d7175] bg-[#f6f6f7] border border-[#e1e3e5] rounded-md px-3 py-2 inline-block">
                No custom subdomain configured yet.
              </p>
            )}
          </div>

          <div className="flex-shrink-0">
            <button
              type="button"
              onClick={() => setShowCustomDomainModal(true)}
              className="px-4 py-2 rounded-md text-sm font-medium border border-[#e1e3e5] text-[#202223] hover:bg-[#f6f6f7] whitespace-nowrap"
            >
              {customDomain ? 'Change subdomain' : 'Set subdomain'}
            </button>
          </div>
        </div>
      </section>

      <CustomDomainModal
        show={showCustomDomainModal}
        setShowModal={(val) => setShowCustomDomainModal(val)}
        customDomain={customDomain}
        setCustomDomain={setCustomDomain}
        handleSave={handleSaveCustomDomain}
        handleDelete={handleDeleteCustomDomain}
      />
    </>
  );
});
